/**
 * Wizard movimento guidato: 2 pulsanti (Salva bozza | Invia a RENTRI).
 *
 * - "Invia a RENTRI": valida (locale) -> se ok crea+invia; in caso di errore RENTRI
 *   il dialog NON si chiude e mostra il messaggio inline; l'utente puo' correggere e
 *   ritentare.
 * - "Salva bozza": persiste la bozza locale e chiude.
 * - Edit bozza: passare `draft` per riempire il form e usare gli endpoint update.
 */
import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { endpoints } from "@/api/client";
import type { ValidationIssue, DraftItem } from "@/api/client";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { MovimentoFormPanel } from "@/components/MovimentoFormPanel";
import {
  applyCerToMovimentoForm,
  defaultCerEntry,
  useCerAttiviList,
} from "@/lib/cerAttiviChoices";
import {
  buildMovimentoPayload,
  defaultMovimentoForm,
  payloadToMovimentoForm,
} from "@/lib/movimentoForm";
import { rentriModelStateMessage } from "@/lib/movimentoUi";

export type { MovimentoFormState } from "@/lib/movimentoForm";

interface Props {
  registroId: string;
  draft?: DraftItem | null;
  onClose: () => void;
}

function titleFor(form: { tipo: string; eer: string; data: string }): string {
  return `Movimento ${form.tipo} EER ${form.eer} del ${form.data}`;
}

function extractRentriError(body: unknown): string | null {
  if (!body) return null;
  if (typeof body === "string") return body;
  if (typeof body !== "object") return null;
  const b = body as Record<string, unknown>;
  const detail = b.detail ?? b.title ?? b.message;
  if (typeof detail === "string") return detail;
  if (detail && typeof detail === "object") {
    const d = detail as Record<string, unknown>;
    for (const k of ["title", "detail", "message"]) {
      if (typeof d[k] === "string") return d[k] as string;
    }
  }
  const campi = rentriModelStateMessage(b);
  if (campi) return campi;
  if (typeof b.raw === "string") return b.raw;
  return null;
}

export function MovimentoWizardDialog({ registroId, draft, onClose }: Props) {
  const qc = useQueryClient();
  const isEdit = Boolean(draft);
  const [form, setForm] = useState(() =>
    draft ? payloadToMovimentoForm(draft.payload) : defaultMovimentoForm(),
  );
  const [issues, setIssues] = useState<ValidationIssue[]>([]);
  const [rentriError, setRentriError] = useState<string | null>(null);

  const cerQ = useCerAttiviList();

  useEffect(() => {
    if (draft) {
      setForm(payloadToMovimentoForm(draft.payload));
    }
  }, [draft]);

  useEffect(() => {
    if (draft || !cerQ.data?.items.length) return;
    const entry = defaultCerEntry(cerQ.data.items);
    if (!entry) return;
    setForm((prev) => {
      if (prev.eer) return prev;
      return applyCerToMovimentoForm(prev, entry);
    });
  }, [draft, cerQ.data?.items]);

  const invalidateRegistri = () => {
    qc.invalidateQueries({ queryKey: ["movimenti", registroId] });
    qc.invalidateQueries({ queryKey: ["drafts"] });
  };

  const sendMut = useMutation({
    mutationFn: async () => {
      setIssues([]);
      setRentriError(null);
      const payload = await buildMovimentoPayload(form, draft?.payload);

      // 1. Validazione locale (best-effort: non blocca se il servizio fallisce)
      try {
        const v = await endpoints.validate.movimento(payload);
        if (!v.valid) {
          return { kind: "invalid" as const, issues: v.issues, payload };
        }
      } catch {
        /* ignora errori del validatore locale */
      }

      // 2. Se sto modificando una bozza esistente, aggiorno e poi invio
      if (draft) {
        await endpoints.registri.aggiornaBozzaMovimento(registroId, draft.id, {
          payload,
          title: titleFor(form),
        });
        const sent = await endpoints.registri.inviaMovimento(registroId, draft.id);
        return { kind: "sent" as const, ...sent };
      }

      // 3. Crea + invia atomicamente
      const sent = await endpoints.registri.creaEInviaMovimento(
        registroId,
        payload,
        titleFor(form),
      );
      return { kind: "sent" as const, ...sent };
    },
    onSuccess: (res) => {
      if (res.kind === "invalid") {
        setIssues(res.issues);
        toast.warning("Compila o correggi i campi evidenziati");
        return;
      }
      // res.kind === 'sent'
      const status = res.rentri_status;
      if (status >= 400) {
        const msg =
          extractRentriError(res.rentri_body) ?? `RENTRI ha rifiutato (HTTP ${status})`;
        setRentriError(msg);
        toast.error("Invio rifiutato da RENTRI: correggi e riprova");
        invalidateRegistri();
        return;
      }
      toast.success(
        status === 202 ? "Movimento in elaborazione su RENTRI" : "Movimento inviato",
      );
      invalidateRegistri();
      onClose();
    },
    onError: (e: unknown) => {
      const msg = e instanceof Error ? e.message : "Errore invio";
      setRentriError(msg);
      toast.error(msg);
    },
  });

  const saveDraftMut = useMutation({
    mutationFn: async () => {
      setIssues([]);
      setRentriError(null);
      const payload = await buildMovimentoPayload(form, draft?.payload);
      if (draft) {
        return endpoints.registri.aggiornaBozzaMovimento(registroId, draft.id, {
          payload,
          title: titleFor(form),
        });
      }
      return endpoints.registri.creaBozzaMovimento(registroId, payload, titleFor(form));
    },
    onSuccess: () => {
      toast.success("Bozza salvata");
      invalidateRegistri();
      onClose();
    },
    onError: (e: unknown) =>
      toast.error(e instanceof Error ? e.message : "Errore salvataggio bozza"),
  });

  const pending = sendMut.isPending || saveDraftMut.isPending;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="flex max-h-[min(92vh,760px)] max-w-3xl flex-col overflow-hidden">
        <DialogHeader className="shrink-0">
          <DialogTitle>
            {isEdit ? "Modifica movimento" : "Nuovo movimento carico/scarico"}
          </DialogTitle>
          <DialogDescription className="sr-only">
            Compila il form e scegli Salva bozza oppure Invia a RENTRI.
          </DialogDescription>
        </DialogHeader>

        {rentriError && (
          <div
            role="alert"
            className="shrink-0 rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-destructive"
          >
            <strong>Errore RENTRI:</strong> {rentriError}
          </div>
        )}

        <MovimentoFormPanel
          form={form}
          onChange={setForm}
          issues={issues}
          className="flex min-h-0 flex-1 flex-col overflow-hidden"
        />

        <DialogFooter className="shrink-0 gap-2">
          <Button variant="outline" onClick={onClose} disabled={pending}>
            Annulla
          </Button>
          <Button
            variant="secondary"
            disabled={pending}
            onClick={() => saveDraftMut.mutate()}
          >
            Salva bozza
          </Button>
          <Button disabled={pending} onClick={() => sendMut.mutate()}>
            {sendMut.isPending ? "Invio…" : "Invia a RENTRI"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
